/**
 * DocumentsTab — Knit's employee DOCUMENTS tab: General Documents /
 * Year End Documents / Record of Employment sub-tabs. General holds
 * Company Uploads + My Uploads tables (name, uploader, viewable, actions)
 * with real upload/download/delete. Year End lists auto-generated exit
 * snapshots. Record of Employment lists ROE drafts and opens CREATE ROE.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MoreVertical, Plus } from "lucide-react";

const SUBTABS = ["General Documents", "Year End Documents", "Record of Employment"];

export default function DocumentsTab({ employeeId, onCreateRoe, onOpenRoe }) {
  const [sub, setSub] = useState("General Documents");
  const [files, setFiles] = useState([]);
  const [roes, setRoes] = useState([]);
  const [menuFor, setMenuFor] = useState(null);
  const uploadFor = useRef(null); // "company" | "mine"
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([
        api.get(`/employees/${employeeId}/files`),
        api.get(`/employees/${employeeId}/roes`),
      ]);
      setFiles(f.data.files || []);
      setRoes(r.data.roes || []);
    } catch {
      toast.error("Could not load documents");
    }
  }, [employeeId]);
  useEffect(() => { load(); }, [load]);

  const pickFile = (uploader) => {
    uploadFor.current = uploader;
    fileInput.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("files", file);
    fd.append("doc_category", "general");
    fd.append("uploader", uploadFor.current || "company");
    fd.append("viewable", "true");
    try {
      await api.post(`/employees/${employeeId}/files`, fd);
      toast.success(`${file.name} uploaded`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    }
  };

  const openFile = async (f) => {
    try {
      const res = await api.get(`/employees/${employeeId}/files/${f.file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Could not open document");
    }
  };

  const toggleViewable = async (f) => {
    try {
      await api.patch(`/employees/${employeeId}/files/${f.file_id}`, { viewable: !f.viewable });
      setFiles((p) => p.map((x) => (x.file_id === f.file_id ? { ...x, viewable: !f.viewable } : x)));
    } catch {
      toast.error("Could not update");
    }
  };

  const deleteFile = async (f) => {
    setMenuFor(null);
    try {
      await api.delete(`/employees/${employeeId}/files/${f.file_id}`);
      toast.success(`${f.name} deleted`);
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const UploadTable = ({ title, uploader, testid }) => {
    const rows = files.filter((f) => f.kind === "document" && (f.uploader || "company") === uploader);
    return (
      <div className="rounded-lg border border-white/10" data-testid={testid}>
        <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">{title}</p>
          <button onClick={() => pickFile(uploader)} data-testid={`${testid}-add`}
            className="rounded-md bg-amber-600 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white hover:bg-amber-500">
            Add Document
          </button>
        </div>
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Uploader</th>
              <th className="px-3 py-2">Viewable</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.file_id} className="border-b border-white/5 last:border-0" data-testid={`doc-row-${f.file_id}`}>
                <td className="px-3 py-2 text-white/80">{f.name}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-white/40">{f.uploaded_by || "—"}</td>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={!!f.viewable} onChange={() => toggleViewable(f)}
                    data-testid={`doc-viewable-${f.file_id}`} className="h-3.5 w-3.5 accent-amber-500" />
                </td>
                <td className="relative px-3 py-2 text-right">
                  <button onClick={() => setMenuFor(menuFor === f.file_id ? null : f.file_id)}
                    data-testid={`doc-actions-${f.file_id}`}
                    className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                  {menuFor === f.file_id && (
                    <div className="absolute right-2 top-8 z-20 w-36 rounded-md border border-white/10 bg-[#18181d] py-1 text-left shadow-xl">
                      <button onClick={() => { setMenuFor(null); openFile(f); }} data-testid={`doc-view-${f.file_id}`}
                        className="block w-full px-3 py-1.5 text-left text-xs text-white/80 hover:bg-white/5">View Document</button>
                      <button onClick={() => deleteFile(f)} data-testid={`doc-delete-${f.file_id}`}
                        className="block w-full px-3 py-1.5 text-left text-xs text-red-400 hover:bg-white/5">Delete</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={4} className="px-3 py-8 text-center text-xs text-white/30">No Document</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div data-testid="profile-documents">
      <input ref={fileInput} type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.csv" className="hidden" onChange={onFileChosen} />
      <div className="mb-4 flex gap-1 border-b border-white/10" role="tablist">
        {SUBTABS.map((t) => (
          <button key={t} role="tab" aria-selected={sub === t} onClick={() => setSub(t)}
            data-testid={`docs-subtab-${t.toLowerCase().replace(/\s/g, "-")}`}
            className={`-mb-px border-b-2 px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${sub === t ? "border-[#f97316] text-white" : "border-transparent text-white/40 hover:text-white/70"}`}>
            {t}
          </button>
        ))}
      </div>

      {sub === "General Documents" && (
        <div className="grid gap-4 md:grid-cols-2">
          <UploadTable title="Company Uploads" uploader="company" testid="company-uploads" />
          <UploadTable title="My Uploads" uploader="mine" testid="my-uploads" />
        </div>
      )}

      {sub === "Year End Documents" && (
        <div className="rounded-lg border border-white/10" data-testid="year-end-docs">
          <p className="border-b border-white/10 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white/50">Year End Documents</p>
          <table className="w-full text-left text-sm">
            <tbody>
              {files.filter((f) => f.kind === "exit_snapshot").map((f) => (
                <tr key={f.file_id} className="border-b border-white/5 last:border-0">
                  <td className="px-3 py-2 text-white/80">{f.name}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/40">{(f.uploaded_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => openFile(f)} data-testid={`yearend-view-${f.file_id}`}
                      className="font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:text-white">view</button>
                  </td>
                </tr>
              ))}
              {!files.some((f) => f.kind === "exit_snapshot") && (
                <tr><td className="px-3 py-8 text-center text-xs text-white/30">No Document</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {sub === "Record of Employment" && (
        <div className="rounded-lg border border-white/10" data-testid="roe-docs">
          <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Record of Employment</p>
            <button onClick={onCreateRoe} data-testid="create-roe-btn"
              className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 font-mono text-[9px] font-bold uppercase tracking-widest text-white hover:bg-amber-500">
              <Plus className="h-3 w-3" /> Create ROE
            </button>
          </div>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-widest text-white/40">
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Updated</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {roes.map((r) => (
                <tr key={r.roe_id} className="border-b border-white/5 last:border-0" data-testid={`roe-row-${r.roe_id}`}>
                  <td className="px-3 py-2 text-white/80">{r.reason || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest ${r.status === "ready" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                      {r.status || "draft"}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-white/40">{(r.updated_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => onOpenRoe(r)} data-testid={`roe-open-${r.roe_id}`}
                      className="font-mono text-[10px] uppercase tracking-widest text-[#8fa4f0] hover:text-white">open</button>
                  </td>
                </tr>
              ))}
              {!roes.length && (
                <tr><td colSpan={4} className="px-3 py-8 text-center text-xs text-white/30">No ROE drafts yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
